import { Fragment, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppStateContext } from "@/state/AppStateContext";
import { getSearchConversations } from "@/api/api";
import { Conversation, SearchConversation } from "@/api/models";
import { clearAgent, clearChatTransientState, clearCustomGPT, resetChat } from "@/utils/utils";
import { CornerDownLeft, Pin, Search, Users, X } from "lucide-react";

type SearchResult = SearchConversation & {
  isGroup?: boolean;
  participantCount?: number;
};

type Section = {
  key: string;
  label: string;
  items: SearchResult[];
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Wraps every occurrence of the query in the title so the reason a result
// matched is visible at a glance.
const highlight = (text: string, query: string): ReactNode => {
  const needle = query.trim();
  if (!needle) return text;

  const parts = text.split(new RegExp(`(${escapeRegExp(needle)})`, "ig"));
  return parts.map((part, idx) =>
    part.toLowerCase() === needle.toLowerCase()
      ? <mark key={idx} className="bg-primary/25 text-inherit rounded-[3px] px-0.5">{part}</mark>
      : <Fragment key={idx}>{part}</Fragment>
  );
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return date.toLocaleDateString(undefined, { weekday: "short" });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const toSearchResult = (chat: Conversation): SearchResult => ({
  id: chat.id,
  title: chat.title,
  pinned: chat.pinned || 0,
  date: chat.date,
  isGroup: true,
  participantCount: chat.participants?.length || 0,
});

const SearchPopup = () => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchConversation[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const latestQueryRef = useRef('');

  const isOpen = !!appStateContext?.state.searchPopupOpen;
  const chatHistory = appStateContext?.state.chatHistory;
  const groupChats = appStateContext?.state.groupChats;

  // Update search results when chat history changes
  useEffect(() => {
    setResults((chatHistory || []).sort((a, b) => (b.pinned || 0) - (a.pinned || 0)));
  }, [chatHistory]);

  // Handle opening search popup
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  }, [isOpen]);

  // Handle search input changes
  useEffect(() => {
    latestQueryRef.current = query;
    setActiveIndex(0);
    if (query.trim().length === 0) {
      setResults((chatHistory || []).sort((a, b) => (b.pinned || 0) - (a.pinned || 0)));
      setIsLoading(false);
      return;
    }

    // Use a timeout to debounce the search input
    const handler = setTimeout(() => {
      handleSearch(query);
    }, 300);
    return () => clearTimeout(handler);
  }, [query]);

  // Group chats live in app state rather than the conversation search: the user
  // may be a participant instead of the owner, so the server-side query (scoped
  // to the caller's own partition) would not return them.
  const groupResults = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (groupChats || [])
      .filter(chat => !needle || (chat.title || "").toLowerCase().includes(needle))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map(toSearchResult);
  }, [groupChats, query]);

  const sections = useMemo<Section[]>(() => {
    const list: Section[] = [];
    if (groupResults.length) list.push({ key: 'groups', label: 'Group chats', items: groupResults });
    if (results.length) list.push({ key: 'chats', label: 'Chats', items: results });
    return list;
  }, [groupResults, results]);

  // Flattened, in render order, so the arrow keys can walk across sections.
  const flatResults = useMemo(() => sections.flatMap(section => section.items), [sections]);
  const hasResults = flatResults.length > 0;
  // Index 0 is the "New chat" row; results start at 1.
  const optionCount = flatResults.length + 1;

  // Handle searching
  const handleSearch = async (search: string) => {
    setIsLoading(true);
    const conversations = await getSearchConversations(search);

    // Only update results if the query matches the latest
    if (search === latestQueryRef.current) {
      setResults(conversations);
      setIsLoading(false);
    }
  }

  // Handle closing popup
  const handleClose = () => {
    appStateContext?.dispatch({ type: 'SET_SEARCH_POPUP_OPEN', payload: false });
  }

  // Handle get all conversation details to open
  const handleLoadChat = (id: string) => {
    handleClose();
    navigate('c/' + id);
  }

  // Group chats live on their own route and need the transient chat state
  // cleared, the same way the sidebar opens them.
  const handleLoadGroupChat = (id: string) => {
    const conversation = (groupChats || []).find(chat => chat.id === id);
    handleClose();
    clearChatTransientState(appStateContext);
    clearCustomGPT(appStateContext);
    clearAgent(appStateContext);
    if (conversation) appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
    navigate('/gc/' + id);
  }

  const handleNewChat = () => {
    handleClose();
    navigate('/');
    resetChat(appStateContext);
  }

  const handleOpenResult = (result: SearchResult) => {
    if (result.isGroup) handleLoadGroupChat(result.id);
    else handleLoadChat(result.id);
  }

  // Keep the highlighted row inside the scroll container as it moves.
  useEffect(() => {
    itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(idx => (idx + 1) % optionCount);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(idx => (idx - 1 + optionCount) % optionCount);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex === 0) {
        handleNewChat();
        return;
      }
      const result = flatResults[activeIndex - 1];
      if (result) handleOpenResult(result);
    }
  }

  if (!isOpen) return null;

  // Running offset so each row knows its position in the flattened list.
  let optionIndex = 0;

  return (
    <div
      className="flex justify-center items-start fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] px-4 pt-[12vh]"
      style={{ animation: 'fade-in 140ms ease-out both' }}
      onClick={handleClose}
    >
      <div
        className="bg-secondary border border-line rounded-3xl shadow-short w-full max-w-xl md:max-w-2xl overflow-hidden"
        style={{ animation: 'pop-in 160ms cubic-bezier(0.22, 1, 0.36, 1) both' }}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Search conversations"
      >
        <div className="flex items-center gap-3 border-b border-line px-5 py-4">
          <Search size={18} className="shrink-0 text-ink-3" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 min-w-0 bg-transparent text-[15px] dark:text-white placeholder:text-ink-3 outline-none"
            placeholder="Search chats and group chats..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {isLoading && <span className="loading loading-spinner loading-xs text-primary shrink-0"></span>}
          {query && !isLoading && (
            <button
              type="button"
              className="flex items-center justify-center rounded-full text-ink-3 hover:text-ink hover:bg-neutral cursor-pointer shrink-0 p-1"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              aria-label="Clear search"
            >
              <X size={15} />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center rounded-md border border-line text-[10px] text-ink-3 leading-none shrink-0 px-1.5 py-1">ESC</kbd>
        </div>

        <div className="max-h-[min(60vh,26rem)] overflow-y-auto scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8 px-2 py-2">
          <button
            type="button"
            ref={el => { itemRefs.current[0] = el; }}
            className={`flex items-center rounded-xl cursor-pointer dark:text-white text-sm w-full gap-3 px-3 py-2.5 ${activeIndex === 0 ? 'bg-neutral' : 'hover:bg-neutral'}`}
            onMouseEnter={() => setActiveIndex(0)}
            onClick={handleNewChat}
          >
            <span className="flex items-center justify-center rounded-lg bg-inset text-ink-2 shrink-0 w-7 h-7">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M2.6687 11.333V8.66699C2.6687 7.74455 2.66841 7.01205 2.71655 6.42285C2.76533 5.82612 2.86699 5.31731 3.10425 4.85156L3.25854 4.57617C3.64272 3.94975 4.19392 3.43995 4.85229 3.10449L5.02905 3.02149C5.44666 2.84233 5.90133 2.75849 6.42358 2.71582C7.01272 2.66769 7.74445 2.66797 8.66675 2.66797H9.16675C9.53393 2.66797 9.83165 2.96586 9.83179 3.33301C9.83179 3.70028 9.53402 3.99805 9.16675 3.99805H8.66675C7.7226 3.99805 7.05438 3.99834 6.53198 4.04102C6.14611 4.07254 5.87277 4.12568 5.65601 4.20313L5.45581 4.28906C5.01645 4.51293 4.64872 4.85345 4.39233 5.27149L4.28979 5.45508C4.16388 5.7022 4.08381 6.01663 4.04175 6.53125C3.99906 7.05373 3.99878 7.7226 3.99878 8.66699V11.333C3.99878 12.2774 3.99906 12.9463 4.04175 13.4688C4.08381 13.9833 4.16389 14.2978 4.28979 14.5449L4.39233 14.7285C4.64871 15.1465 5.01648 15.4871 5.45581 15.7109L5.65601 15.7969C5.87276 15.8743 6.14614 15.9265 6.53198 15.958C7.05439 16.0007 7.72256 16.002 8.66675 16.002H11.3337C12.2779 16.002 12.9461 16.0007 13.4685 15.958C13.9829 15.916 14.2976 15.8367 14.5447 15.7109L14.7292 15.6074C15.147 15.3511 15.4879 14.9841 15.7117 14.5449L15.7976 14.3447C15.8751 14.128 15.9272 13.8546 15.9587 13.4688C16.0014 12.9463 16.0017 12.2774 16.0017 11.333V10.833C16.0018 10.466 16.2997 10.1681 16.6667 10.168C17.0339 10.168 17.3316 10.4659 17.3318 10.833V11.333C17.3318 12.2555 17.3331 12.9879 17.2849 13.5771C17.2422 14.0993 17.1584 14.5541 16.9792 14.9717L16.8962 15.1484C16.5609 15.8066 16.0507 16.3571 15.4246 16.7412L15.1492 16.8955C14.6833 17.1329 14.1739 17.2354 13.5769 17.2842C12.9878 17.3323 12.256 17.332 11.3337 17.332H8.66675C7.74446 17.332 7.01271 17.3323 6.42358 17.2842C5.90135 17.2415 5.44665 17.1577 5.02905 16.9785L4.85229 16.8955C4.19396 16.5601 3.64271 16.0502 3.25854 15.4238L3.10425 15.1484C2.86697 14.6827 2.76534 14.1739 2.71655 13.5771C2.66841 12.9879 2.6687 12.2555 2.6687 11.333ZM13.4646 3.11328C14.4201 2.334 15.8288 2.38969 16.7195 3.28027L16.8865 3.46485C17.6141 4.35685 17.6143 5.64423 16.8865 6.53613L16.7195 6.7207L11.6726 11.7686C11.1373 12.3039 10.4624 12.6746 9.72827 12.8408L9.41089 12.8994L7.59351 13.1582C7.38637 13.1877 7.17701 13.1187 7.02905 12.9707C6.88112 12.8227 6.81199 12.6134 6.84155 12.4063L7.10132 10.5898L7.15991 10.2715C7.3262 9.53749 7.69692 8.86241 8.23218 8.32715L13.2791 3.28027L13.4646 3.11328ZM15.7791 4.2207C15.3753 3.81702 14.7366 3.79124 14.3035 4.14453L14.2195 4.2207L9.17261 9.26856C8.81541 9.62578 8.56774 10.0756 8.45679 10.5654L8.41772 10.7773L8.28296 11.7158L9.22241 11.582L9.43433 11.543C9.92426 11.432 10.3749 11.1844 10.7322 10.8271L15.7791 5.78027L15.8552 5.69629C16.185 5.29194 16.1852 4.708 15.8552 4.30371L15.7791 4.2207Z"></path></svg>
            </span>
            <span className="flex-1 text-left">New chat</span>
            <CornerDownLeft size={14} className={`text-ink-3 shrink-0 ${activeIndex === 0 ? 'opacity-100' : 'opacity-0'}`} />
          </button>

          {isLoading ? (
            Array(6).fill(0).map((_, idx) => (
              <div key={idx} className="flex items-center w-full gap-3 px-3 py-2.5 animate-pulse">
                <div className="w-7 h-7 bg-accent/50 rounded-lg shrink-0"></div>
                <div className="h-3 bg-accent/50 rounded" style={{ width: `${70 - idx * 8}%` }}></div>
              </div>
            ))
          ) : !hasResults ? (
            <div className="flex flex-col items-center text-center gap-2 px-4 py-10">
              <span className="flex items-center justify-center rounded-full bg-inset text-ink-3 w-10 h-10">
                <Search size={18} />
              </span>
              <h6 className="text-sm dark:text-white">No results</h6>
              <p className="text-xs text-ink-3">
                {query ? <>Nothing matches &ldquo;{query}&rdquo;</> : <>Start a new chat to see it here</>}
              </p>
            </div>
          ) : (
            sections.map(section => (
              <div key={section.key} className="mt-3 first:mt-2">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-3 px-3 pb-1">
                  {section.label}
                  <span className="normal-case tracking-normal opacity-70">{section.items.length}</span>
                </div>

                {section.items.map(result => {
                  const index = ++optionIndex;
                  const isActive = activeIndex === index;

                  return (
                    <div
                      key={`${section.key}-${result.id}`}
                      ref={el => { itemRefs.current[index] = el; }}
                      role="option"
                      aria-selected={isActive}
                      className={`flex justify-between items-center rounded-xl cursor-pointer w-full gap-3 px-3 py-2.5 ${isActive ? 'bg-neutral' : 'hover:bg-neutral'}`}
                      style={{ animation: 'fade-up 160ms ease-out both' }}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => handleOpenResult(result)}
                    >
                      <div className="flex items-center min-w-0 gap-3">
                        <span className={`flex items-center justify-center rounded-lg shrink-0 w-7 h-7 ${result.isGroup ? 'bg-primary/15 text-primary' : 'bg-inset text-ink-2'}`}>
                          {result.isGroup ? (
                            <Users size={15} />
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-rtl-flip=""><path d="M16.835 9.99968C16.8348 6.49038 13.8111 3.58171 10 3.58171C6.18893 3.58171 3.16523 6.49038 3.16504 9.99968C3.16504 11.4535 3.67943 12.7965 4.55273 13.8766C4.67524 14.0281 4.72534 14.2262 4.68945 14.4176C4.59391 14.9254 4.45927 15.4197 4.30469 15.904C4.93198 15.8203 5.5368 15.6959 6.12793 15.528L6.25391 15.5055C6.38088 15.4949 6.5091 15.5208 6.62305 15.5817C7.61731 16.1135 8.76917 16.4186 10 16.4186C13.8112 16.4186 16.835 13.5091 16.835 9.99968ZM18.165 9.99968C18.165 14.3143 14.4731 17.7487 10 17.7487C8.64395 17.7487 7.36288 17.4332 6.23438 16.8757C5.31485 17.118 4.36919 17.2694 3.37402 17.3307C3.14827 17.3446 2.93067 17.2426 2.79688 17.0602C2.66303 16.8778 2.63177 16.6396 2.71289 16.4284L2.91992 15.863C3.08238 15.3953 3.21908 14.9297 3.32227 14.4606C2.38719 13.2019 1.83496 11.6626 1.83496 9.99968C1.83515 5.68525 5.52703 2.25163 10 2.25163C14.473 2.25163 18.1649 5.68525 18.165 9.99968Z"></path></svg>
                          )}
                        </span>
                        <div className="min-w-0">
                          <h6 className="truncate text-sm dark:text-white">{highlight(result.title || 'Untitled', query)}</h6>
                          {result.isGroup && !!result.participantCount && (
                            <span className="text-[11px] text-ink-3">
                              {result.participantCount} {result.participantCount === 1 ? 'participant' : 'participants'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        {result.pinned == 1 && <Pin size={14} className="text-ink-3" />}
                        <span className="text-xs text-ink-3 tabular-nums">{formatDate(result.date)}</span>
                        <CornerDownLeft size={14} className={`text-ink-3 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="hidden sm:flex items-center gap-4 border-t border-line text-[11px] text-ink-3 px-5 py-2.5">
          <span className="flex items-center gap-1.5">
            <kbd className="rounded border border-line px-2 py-0.5">↑</kbd>
            <kbd className="rounded border border-line px-2 py-0.5">↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="rounded border border-line px-2 py-0.5">Enter</kbd>
            open
          </span>
          <span className="ms-auto">
            {flatResults.length} {flatResults.length === 1 ? 'result' : 'results'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default SearchPopup;
